import React from 'react';

// DO-3828 shared multi-stage UI helpers: status -> pill/icon variant and the
// small inline glyphs used by the case-icon circle and follow-up opt-cards.
// Mirrors the prototype CaseIcon() and option-card icons.

export type StatusVariant = 'complete' | 'inprogress' | 'accepted' | 'assigned' | 'upcoming' | 'paused' | 'canceled';

export const statusVariant = (status: string): StatusVariant => {
  const s = (status || '').toLowerCase();
  if (s.includes('cancel')) return 'canceled';
  if (s.includes('complet')) return 'complete';
  if (s.includes('paus')) return 'paused';
  if (s === 'new' || s.startsWith('not') || s.includes('pending') || s.includes('upcoming')) return 'upcoming';
  // DO-4518: a case the care advisor/provider has only accepted/been assigned
  // (accepted_care_advisor / accepted_provider) hasn't been launched yet — it
  // must not read as "in progress" until that role actually starts it
  // (in_progress_care_advisor / in_progress_provider). The two accepted_*
  // values get distinct buckets, matching the admin Cases grid's own
  // getColorForStatus.ts convention: care-advisor-owned work (care
  // coordination, oral health coaching, …) reads "Accepted", provider-owned
  // clinical work reads "Assigned".
  if (s.startsWith('accepted_care_advisor')) return 'accepted';
  if (s.startsWith('accepted_provider')) return 'assigned';
  return 'inprogress';
};

// Staff-facing status label. Raw case statuses (new, accepted_care_advisor,
// in_progress_provider, …) and the program's own status values (in_progress,
// paused, complete, canceled) are collapsed into the small, patient-legible
// vocabulary the design uses — Complete / In progress / Accepted / Assigned /
// Upcoming / Paused / Canceled — so the profile Cases view reads the same as
// the rest of the journey UI. "new" (incl. a scheduled case with no time yet)
// reads as "Upcoming". A case only accepted/assigned to a care advisor or
// provider — not yet launched by them — reads as "Accepted" or "Assigned"
// rather than "In progress" (DO-4518), matching getTextForStatus.ts's admin
// mapping (AcceptedCareAdvisor -> Accepted, AcceptedProvider -> Assigned).
// Anything outside the clinical flow (e.g. payment_errored) falls back to a
// title-cased form rather than being mislabeled.
export const statusLabel = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s.includes('cancel')) return 'Canceled';
  // The master design system labels a finished case "Complete", not "Completed".
  if (s.includes('complete')) return 'Complete';
  if (s.includes('paus')) return 'Paused';
  if (s === 'new' || s.startsWith('not') || s.includes('pending') || s.includes('upcoming')) {
    return 'Upcoming';
  }
  if (s.startsWith('accepted_care_advisor')) {
    return 'Accepted';
  }
  if (s.startsWith('accepted_provider')) {
    return 'Assigned';
  }
  if (s === 'in progress' || s.startsWith('in_progress')) {
    return 'In progress';
  }
  return status.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const svg = (children: React.ReactNode, viewBox = '0 0 24 24'): JSX.Element => (
  <svg
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

// Status glyph inside the 34px case-icon circle (prototype: Check/Play/Clock/X).
export const StatusGlyph = ({ variant }: { variant: StatusVariant }): JSX.Element => {
  if (variant === 'complete') return svg(<polyline points="20 6 9 17 4 12" />);
  if (variant === 'canceled')
    return svg(
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>,
    );
  if (variant === 'upcoming')
    return svg(
      <>
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
      </>,
    );
  if (variant === 'paused')
    return svg(
      <>
        <rect x="6" y="4" width="4" height="16" fill="currentColor" stroke="none" />
        <rect x="14" y="4" width="4" height="16" fill="currentColor" stroke="none" />
      </>,
    );
  // Accepted (by a care advisor) and assigned (to a provider) share the same
  // person glyph — accepted/assigned but not yet started — and are told apart
  // by pill/icon color alone, same as the admin Cases grid.
  if (variant === 'accepted' || variant === 'assigned')
    return svg(
      <>
        <circle cx="12" cy="8" r="3.4" />
        <path d="M5.5 20c0-3.6 2.9-6.4 6.5-6.4s6.5 2.8 6.5 6.4" />
      </>,
    );
  return svg(<polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />); // inprogress: play
};

export const CaseIcon = ({ status }: { status: string }): JSX.Element => {
  const v = statusVariant(status);
  return (
    <div className={`ms-case-icon ${v}`}>
      <StatusGlyph variant={v} />
    </div>
  );
};

// Type glyph for the follow-up opt-cards.
export const TypeIcon = ({ type }: { type: string }): JSX.Element => {
  switch (type) {
    case 'video_call_scheduled':
    case 'video_call_instant':
      return svg(
        <path
          d="M15 8V16H5V8H15ZM16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L19.2929 15.7929C19.9229 16.4229 21 15.9767 21 15.0858V8.91421C21 8.02331 19.9229 7.57714 19.2929 8.20711L17 10.5V7C17 6.45 16.55 6 16 6Z"
          fill="currentColor"
          stroke="none"
        />,
      );
    case 'phone_call_scheduled':
      return svg(
        <path
          d="M11.526 13.8067C11.6981 13.8857 11.892 13.9038 12.0758 13.8579C12.2595 13.812 12.4221 13.7049 12.5368 13.5542L12.8327 13.1667C12.9879 12.9597 13.1892 12.7917 13.4207 12.676C13.6521 12.5603 13.9073 12.5 14.166 12.5H16.666C17.108 12.5 17.532 12.6756 17.8445 12.9882C18.1571 13.3007 18.3327 13.7247 18.3327 14.1667V16.6667C18.3327 17.1087 18.1571 17.5326 17.8445 17.8452C17.532 18.1578 17.108 18.3334 16.666 18.3334C12.6878 18.3334 8.87246 16.753 6.05941 13.94C3.24637 11.1269 1.66602 7.3116 1.66602 3.33335C1.66602 2.89133 1.84161 2.4674 2.15417 2.15484C2.46673 1.84228 2.89065 1.66669 3.33268 1.66669H5.83268C6.27471 1.66669 6.69863 1.84228 7.01119 2.15484C7.32375 2.4674 7.49935 2.89133 7.49935 3.33335V5.83335C7.49935 6.0921 7.43911 6.34728 7.32339 6.57871C7.20768 6.81014 7.03968 7.01144 6.83268 7.16669L6.44268 7.45919C6.2897 7.576 6.18187 7.74217 6.13751 7.92948C6.09315 8.11678 6.115 8.31367 6.19935 8.48669C7.33825 10.7999 9.21137 12.6707 11.526 13.8067Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />,
        '0 0 20 20',
      );
    case 'oral_health_coaching':
      return svg(
        <path
          d="M17.8605 1.5L12.9105 6.43L15.0305 8.55L16.0905 7.5L15.0305 6.43L17.8605 3.6L18.9205 4.66L20.0005 3.6M21.4005 3.6L15.5605 9.44L13.4305 10.15L3.00055 20.57L4.43055 22L10.0805 16.33L14.3305 20.57L18.5705 16.33L14.3305 12.09L14.5005 11.9L16.6305 11.19L21.4005 6.43C22.1805 5.65 22.1805 4.38 21.4005 3.6ZM6.55055 4.31L2.31055 8.55L7.26055 13.5L11.5005 9.26M18.2205 18.1L16.0905 20.22L17.5005 21.63L19.6305 19.5L18.2205 18.1Z"
          fill="currentColor"
          stroke="none"
        />,
      );
    case 'care_coordination':
      return svg(
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19.8 10.18C19.49 7.00002 17.61 2 11.8 2C5.99002 2 4.11001 7.00002 3.80002 10.18C2.71252 10.5927 1.9952 11.6368 2.00002 12.8V14.2C2.00002 15.7464 3.25365 17 4.80001 17C6.34642 17 7.60004 15.7464 7.60004 14.2V12.8C7.59498 11.6619 6.90404 10.6393 5.85001 10.21C6.05002 8.36998 7.03004 4.00002 11.8 4.00002C16.57 4.00002 17.54 8.36998 17.74 10.21C16.6882 10.6402 16.0007 11.6636 16 12.8V14.2C16.0022 14.7269 16.1524 15.2425 16.4335 15.6881C16.7147 16.1337 17.1154 16.4913 17.59 16.72C17.17 17.51 16.1 18.58 13.47 18.9C12.9443 18.1017 11.9272 17.787 11.0424 18.1489C10.1578 18.5108 9.65279 19.4481 9.83729 20.386C10.0218 21.3239 10.8442 22 11.8 22C12.1704 21.9979 12.5329 21.8931 12.8472 21.6971C13.1615 21.5011 13.4152 21.2217 13.58 20.89C17.87 20.4 19.24 18.19 19.67 16.89C20.8333 16.5132 21.6157 15.4227 21.6 14.2V12.8C21.6048 11.6368 20.8875 10.5927 19.8 10.18ZM5.60002 14.2C5.60002 14.6418 5.24185 15 4.80001 15C4.35816 15 4.00004 14.6419 4.00004 14.2V12.8C3.99923 12.6944 4.01933 12.5897 4.05917 12.492C4.09901 12.3942 4.15781 12.3053 4.23217 12.2304C4.30654 12.1554 4.395 12.0959 4.49247 12.0553C4.58993 12.0148 4.69446 11.9939 4.80003 11.9939C4.90561 11.9939 5.01014 12.0148 5.1076 12.0553C5.20506 12.0959 5.29352 12.1554 5.36789 12.2304C5.44226 12.3053 5.50105 12.3942 5.5409 12.492C5.58074 12.5897 5.60083 12.6944 5.60002 12.8V14.2ZM18 12.8C18 12.3582 18.3582 12 18.8 12C19.2419 12 19.6 12.3582 19.6 12.8V14.2C19.6 14.6418 19.2419 15 18.8 15C18.3582 15 18 14.6419 18 14.2V12.8Z"
          fill="currentColor"
          stroke="none"
        />,
      );
    case 'second_opinion':
      return svg(
        <path
          d="M8 17C8 16.4477 8.44772 16 9 16H15C15.5523 16 16 16.4477 16 17C16 17.5523 15.5523 18 15 18H9C8.44772 18 8 17.5523 8 17ZM8 13C8 12.4477 8.44772 12 9 12H15C15.5523 12 16 12.4477 16 13C16 13.5523 15.5523 14 15 14H9C8.44772 14 8 13.5523 8 13ZM14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V8C13 8.55228 13.4477 9 14 9H18V20Z"
          fill="currentColor"
          stroke="none"
        />,
      );
    case 'clinical_enablement':
      return svg(
        <>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <line x1="12" y1="9" x2="12" y2="15" />
          <line x1="9" y1="12" x2="15" y2="12" />
        </>,
      );
    case 'one_report':
      return svg(
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </>,
      );
    default:
      return svg(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </>,
      );
  }
};

// Shared case-type display labels for the multi-stage UI (single source of truth).
// Sentence case, matching the master design system: case types read "Photo
// review" / "Scheduled video call" / "Care coordination" wherever a case is
// named. (The marketing service cards on the patient dashboard are separate
// product names and stay title-cased — they don't come from this map.)
// "SmileScan" keeps its brand capitalization.
export const CASE_TYPE_LABELS: Record<string, string> = {
  video_call_scheduled: 'Scheduled video call',
  video_call_instant: 'Video call',
  phone_call_scheduled: 'Scheduled phone call',
  one_report: 'Photo review',
  second_opinion: 'Second opinion',
  smile_scan: 'SmileScan',
  care_coordination: 'Care coordination',
  oral_health_coaching: 'Oral health coaching',
  clinical_enablement: 'Clinical enablement',
  messaging: 'Messaging',
};

export const caseTypeLabel = (type: string): string => CASE_TYPE_LABELS[type] || type;

// Clinical case types are those a clinician personally handles. Everything else
// (care coordination, oral health coaching, etc.) is non-clinical and is worked
// by coordinators/coaches — so it can't be self-assigned to the acting user.
// Clinical enablement belongs here: the backend has a provider review it,
// exactly like a photo review (DEFAULT_REVIEWER_BY_CASE_TYPE).
const CLINICAL_CASE_TYPES = new Set<string>([
  'video_call_scheduled',
  'video_call_instant',
  'phone_call_scheduled',
  'one_report',
  'second_opinion',
  'clinical_enablement',
]);

export const isClinicalCaseType = (type: string): boolean => CLINICAL_CASE_TYPES.has(type);

// SmileScan is a self-serve screening rather than a case a clinician works,
// so it can't anchor a multi-stage program.
export const isConvertibleCaseType = (type: string): boolean => type !== 'smile_scan';
